'use client';
import { Flex } from 'antd';
import Heading from '../Heading';
import { CrossSellBannerFragment } from '@/@types/generated/sitecore';
import ImageBgSection from '../Template/ImageBgSection';
import CustButton, { GeneralButtonType } from '../Button';
import { useGlobalStore } from '@/hooks/useGlobalStore';
import WaveRightIcon from '../SVG/WaveRightIcon';
import WaveLeftIcon from '../SVG/WaveLeftIcon';
import { WATrackerClickEvn, getButtonLinkWithCID } from '@/utils/wa';
import RichText from '../RichText';
import { useTranslation } from '@i18n/use-transition.client';

interface Props {
    content: CrossSellBannerFragment | any;
    currentThemeColor?: string | undefined;
    currentFontColor?: string;
    eventPageID?: string;
    currentHighlightColor?: string;
    currentPartyPrimaryButtonBGColor?: string;
    currentPartyPrimaryButtonIconColor?: string;
    currentSecondaryButtonBGColor?: string;
    currentSecondaryButtonIconColor?: string;
    currentMusicWaveColor?: string;
}

type PartyItem = {
    name: string;
    partyName?: { value?: string };
    partyDateText?: { value?: string };
};

const CrossSellBanner = (props: Props) => {
    // console.log('homePartner----', props);
    const {
        bannerType,
        bannerBgImage,
        generalHeader,
        generalContent,
        generalCtaEnable,
        generalCtaText,
        generalCtaLink,
        partyLink,
        partyCtaEnable,
        partyCtaLink,
    } = props.content;

    const globalStore = useGlobalStore();
    const { t } = useTranslation();

    const fontColor = globalStore.currentTheme?.fontColor;
    const musicWaveColor = globalStore.currentTheme?.musicWaveColor;
    const highlightColor = globalStore.currentTheme?.highlightColor;

    const TITLE_ICON = {
        titleIconRight: () => <WaveRightIcon iconColor={musicWaveColor}></WaveRightIcon>,
        titleIconLeft: () => <WaveLeftIcon iconColor={musicWaveColor}></WaveLeftIcon>,
    };

    const TitleIcon = props.content?.titleIconOptions?.value
        ? TITLE_ICON[props.content?.titleIconOptions?.value as keyof typeof TITLE_ICON]
        : () => null;

    const isParty = bannerType?.value === 'PartyLink';

    return (
        <ImageBgSection image={bannerBgImage?.src}>
            <Flex gap={23} align='center' vertical className={'my-24'}>
                <Flex vertical gap={8} align='center'>
                    <Heading
                        className={'text-center text-base-font-color'}
                        level={5}
                        text={generalHeader?.value}
                        style={{ color: '#FFFFFF' }}
                    />
                    <TitleIcon />
                </Flex>
                {isParty ? (
                    <>
                        <Heading
                            className={'text-center text-base-font-color'}
                            level={2}
                            text={(partyLink?.targetItem as PartyItem)?.partyName?.value}
                            style={{ color: '#FFFFFF' }}
                        />
                        <Heading
                            className={'text-center text-black'}
                            level={4}
                            text={(partyLink?.targetItem as PartyItem)?.partyDateText?.value}
                            style={{
                                backgroundColor: highlightColor,
                                color: fontColor,
                            }}
                        />
                        {partyCtaEnable?.boolValue && (
                            <CustButton
                                data={{
                                    setting: {
                                        type: 'link',
                                        onClick: () => {
                                            WATrackerClickEvn('RnE/HW_ClickBanner_Section-Banner');
                                        },
                                    },
                                    buttonLink: getButtonLinkWithCID(
                                        partyCtaLink?.value,
                                        `${globalStore.pageID}_Section-Banner`,
                                    ),
                                }}
                            >
                                <GeneralButtonType
                                    label={t('corssSellSchedule')}
                                    icon={'arrowRightPrimary'}
                                    className='!text-base-font-color'
                                />
                            </CustButton>
                        )}
                    </>
                ) : (
                    <>
                        <RichText
                            className={'text-center text-white '}
                            html={generalContent?.rendered || ''}
                        />
                        {generalCtaEnable?.boolValue && (
                            <div className='mt-2'>
                                <CustButton
                                    data={{
                                        buttonLink: getButtonLinkWithCID(
                                            generalCtaLink?.value,
                                            `${globalStore.pageID}_Section-Banner`,
                                        ),
                                        setting: {
                                            onClick: () => {
                                                WATrackerClickEvn(
                                                    'RnE/HW_ClickBanner_Section-Banner',
                                                );
                                            },
                                        },
                                    }}
                                >
                                    <GeneralButtonType
                                        label={generalCtaText?.value}
                                        icon={'arrowRightBK'}
                                        fontColor={props.currentFontColor}
                                        iconBGColor={props.currentPartyPrimaryButtonBGColor}
                                        iconColor={props.currentPartyPrimaryButtonIconColor}
                                        className='text-base-font-color'
                                    />
                                </CustButton>
                            </div>
                        )}
                    </>
                )}
            </Flex>
        </ImageBgSection>
    );
};

export default CrossSellBanner;
